import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errCode } from "@/lib/api";
import { useLang } from "@/i18n";

const CHUNK_MS = 10000;
const UPLOAD_RETRIES = 4;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

async function uploadChunk(uploadId, index, blob) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      const fd = new FormData();
      fd.append("file", blob, `chunk_${index}.webm`);
      fd.append("index", String(index));
      await api.post(`/ai/recordings/${uploadId}/chunk`, fd, { timeout: 60000 });
      return true;
    } catch (err) {
      if (attempt >= UPLOAD_RETRIES) {
        console.error("chunk upload failed", index, err);
        return false;
      }
      await wait(1000 * 2 ** attempt);
    }
  }
}

export function useRecorder({ meetingId, onFinished }) {
  const { t } = useLang();
  const [state, setState] = useState("idle"); // idle | starting | recording | finishing
  const [seconds, setSeconds] = useState(0);
  const secondsRef = useRef(0);
  const recRef = useRef(null);
  const uploadIdRef = useRef(null);
  const indexRef = useRef(0);
  const queueRef = useRef(Promise.resolve());
  const failedRef = useRef(new Map());
  const streamsRef = useRef([]);
  const ctxRef = useRef(null);
  const timerRef = useRef(null);

  const cleanup = useCallback(() => {
    clearInterval(timerRef.current);
    streamsRef.current.forEach((s) => s.getTracks().forEach((tr) => tr.stop()));
    streamsRef.current = [];
    ctxRef.current?.close().catch(() => {});
    ctxRef.current = null;
    recRef.current = null;
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const start = useCallback(async () => {
    setState("starting");
    try {
      const { data } = await api.post("/ai/recordings/start", { meeting_id: meetingId, mime: "audio/webm" });
      uploadIdRef.current = data.upload_id;
      indexRef.current = 0;
      queueRef.current = Promise.resolve();
      failedRef.current = new Map();

      const ctx = new AudioContext();
      ctxRef.current = ctx;
      const dest = ctx.createMediaStreamDestination();

      let tab = null;
      try {
        tab = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true,
          preferCurrentTab: true,
          selfBrowserSurface: "include",
          systemAudio: "include",
        });
      } catch {
        tab = null;
      }
      if (tab && tab.getAudioTracks().length) {
        ctx.createMediaStreamSource(new MediaStream(tab.getAudioTracks())).connect(dest);
        tab.getVideoTracks().forEach((tr) => tr.stop());
        streamsRef.current.push(tab);
      } else {
        tab?.getTracks().forEach((tr) => tr.stop());
        toast.warning(t("room.tabAudioMissing"));
      }
      const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
      ctx.createMediaStreamSource(mic).connect(dest);
      streamsRef.current.push(mic);

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const rec = new MediaRecorder(dest.stream, { mimeType, audioBitsPerSecond: 64000 });
      rec.ondataavailable = (e) => {
        if (!e.data || !e.data.size) return;
        const index = indexRef.current++;
        const uploadId = uploadIdRef.current;
        const blob = e.data;
        queueRef.current = queueRef.current.then(async () => {
          if (!(await uploadChunk(uploadId, index, blob))) failedRef.current.set(index, blob);
        });
      };
      rec.start(CHUNK_MS);
      recRef.current = rec;
      secondsRef.current = 0;
      setSeconds(0);
      timerRef.current = setInterval(() => {
        secondsRef.current += 1;
        setSeconds(secondsRef.current);
      }, 1000);
      setState("recording");
    } catch (e) {
      toast.error(t(`errors.${errCode(e)}`, errCode(e)));
      cleanup();
      setState("idle");
    }
  }, [meetingId, cleanup, t]);

  const stop = useCallback(async () => {
    const rec = recRef.current;
    if (!rec) return;
    setState("finishing");
    clearInterval(timerRef.current);
    await new Promise((resolve) => {
      rec.onstop = resolve;
      rec.stop();
    });
    cleanup();
    await queueRef.current;
    for (const [index, blob] of failedRef.current) {
      if (await uploadChunk(uploadIdRef.current, index, blob)) failedRef.current.delete(index);
    }
    if (failedRef.current.size) toast.warning(t("room.chunksLost", null, { n: failedRef.current.size }));
    try {
      const { data } = await api.post(`/ai/recordings/${uploadIdRef.current}/finish`, {
        chunks: indexRef.current,
        duration: secondsRef.current,
      });
      toast.success(t("room.recSaved"));
      onFinished?.(data);
    } catch (e) {
      toast.error(t(`errors.${errCode(e)}`, errCode(e)));
    } finally {
      setState("idle");
    }
  }, [cleanup, onFinished, t]);

  return { state, seconds, start, stop };
}

export const fmtClock = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
